import { CAD_GEOREF_MAX_POINTS, CAD_GEOREF_MIN_POINTS } from '../data/cadConstants';
import { normalizeCadCanvasImageTransform } from './cadCanvasImage';
import { computeGeometryBbox, unionLngLatBboxes } from './cadGeometry';
import { georefPointsFromSticker } from './cadGeorefSticker';

const CAD_LOT_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * `ref_lot_id` es un `lots.id` de Terrio. No es `cad_projects.id` ni el
 * `id` de una parcela del plano.
 * @param {unknown} value
 * @returns {boolean}
 */
export function isCadRefLotId(value) {
  return CAD_LOT_UUID_RE.test(String(value || '').trim());
}

/**
 * UUID `lots.id` en una feature catastral. No usa Feature.id del plano CAD.
 * @param {object|null|undefined} feature
 * @returns {string}
 */
export function readCadastralLotIdFromFeature(feature) {
  if (!feature || typeof feature !== 'object') return '';
  const props = feature.properties || {};
  const candidates = [props.lot_id, props.lotId, props.uuid, props.id, feature.id];
  const found = candidates.find((value) => isCadRefLotId(value));
  return found ? String(found).trim() : '';
}

function cadRefLotSourceFilter(lotId) {
  const id = String(lotId || '').trim();
  return [
    'any',
    ['==', ['get', 'lot_id'], id],
    ['==', ['get', 'lotId'], id],
    ['==', ['get', 'uuid'], id],
    ['==', ['get', 'id'], id],
  ];
}

/**
 * Bbox del lote madre uniendo fragmentos de tesela (el click solo ve un tile).
 * @param {object[]|null|undefined} features
 * @param {unknown} lotId
 * @returns {[number, number, number, number]|null}
 */
export function cadRefLotBboxFromFeatures(features, lotId) {
  const wanted = String(lotId || '').trim().toLowerCase();
  if (!wanted || !Array.isArray(features)) return null;
  const boxes = [];
  features.forEach((feature) => {
    const found = readCadastralLotIdFromFeature(feature);
    if (!found || found.toLowerCase() !== wanted) return;
    const bbox = computeGeometryBbox(feature?.geometry);
    if (bbox) boxes.push(bbox);
  });
  return unionLngLatBboxes(boxes);
}

/**
 * Contorno del lote en teselas cargadas, sin gastar cuota de detalle.
 * Une `queryRenderedFeatures` + `querySourceFeatures` del mismo `lots.id`.
 * @param {object|null} map
 * @param {unknown} lotId
 * @param {{ layerIds?: string[], sources?: Array<{ sourceId?: string, sourceLayer?: string }> }} [opts]
 * @returns {[number, number, number, number]|null}
 */
export function queryCadRefLotBboxOnMap(map, lotId, { layerIds, sources } = {}) {
  const wanted = String(lotId || '').trim();
  if (!map || !isCadRefLotId(wanted)) return null;
  const boxes = [];
  if (Array.isArray(layerIds) && layerIds.length > 0) {
    try {
      const hits = map.queryRenderedFeatures({ layers: layerIds }) || [];
      const bbox = cadRefLotBboxFromFeatures(hits, wanted);
      if (bbox) boxes.push(bbox);
    } catch (_) {
      // teselas todavía no listas
    }
  }
  if (Array.isArray(sources)) {
    const filter = cadRefLotSourceFilter(wanted);
    sources.forEach((entry) => {
      const sourceId = entry?.sourceId;
      const sourceLayer = entry?.sourceLayer;
      if (!sourceId || !sourceLayer) return;
      try {
        const pieces = map.querySourceFeatures(sourceId, { sourceLayer, filter }) || [];
        const bbox = cadRefLotBboxFromFeatures(pieces, wanted);
        if (bbox) boxes.push(bbox);
      } catch (_) {
        // source recargándose
      }
    });
  }
  return unionLngLatBboxes(boxes);
}

/**
 * @param {unknown} n
 * @param {number} [digits]
 * @returns {number|null}
 */
export function roundCadGeorefCoord(n, digits = 4) {
  const x = Number(n);
  if (!Number.isFinite(x)) return null;
  return Number(x.toFixed(digits));
}

/**
 * @param {unknown} pair
 * @returns {string}
 */
export function formatCadGeorefPair(pair) {
  if (!Array.isArray(pair) || pair.length < 2) return '—';
  const a = roundCadGeorefCoord(pair[0]);
  const b = roundCadGeorefCoord(pair[1]);
  if (a == null || b == null) return '—';
  return `${a}, ${b}`;
}

function isFinitePair(pair) {
  return Array.isArray(pair)
    && pair.length >= 2
    && Number.isFinite(Number(pair[0]))
    && Number.isFinite(Number(pair[1]));
}

/**
 * Puntos con src (metros) y lnglat completos.
 * @param {Array<{ src?: number[], lnglat?: number[] }>|null|undefined} points
 * @returns {{ src: number[], lnglat: number[] }[]}
 */
export function completeCadGeorefPoints(points) {
  return (Array.isArray(points) ? points : [])
    .filter((point) => isFinitePair(point?.src) && isFinitePair(point?.lnglat))
    .slice(0, CAD_GEOREF_MAX_POINTS)
    .map((point) => ({
      src: [Number(point.src[0]), Number(point.src[1])],
      lnglat: [Number(point.lnglat[0]), Number(point.lnglat[1])],
    }));
}

/**
 * Copy cuando Guardar está apagado.
 * @param {{ refLotId?: string, points?: object[], sticker?: object|null }} input
 * @returns {string|null}
 */
export function cadGeorefSubmitHint({ refLotId, points, sticker, hasImage } = {}) {
  if (hasImage || canSubmitCadGeoref({ refLotId, points, sticker })) return null;
  return 'Hacé click en el lote madre para apoyar el plano, o subí una ilustración.';
}

/**
 * Lote madre, sticker en vivo, o 2–3 pares. Si hay sticker/puntos, el bbox se ignora.
 * @param {{ refLotId?: string, points?: object[], sticker?: object|null }} input
 * @returns {boolean}
 */
export function canSubmitCadGeoref({ refLotId, points, sticker } = {}) {
  if (georefPointsFromSticker(sticker).length >= CAD_GEOREF_MIN_POINTS) return true;
  const complete = completeCadGeorefPoints(points);
  if (complete.length > 0 && complete.length < CAD_GEOREF_MIN_POINTS) return false;
  return isCadRefLotId(refLotId) || complete.length >= CAD_GEOREF_MIN_POINTS;
}

/**
 * Hay lote madre o puntos guardados (GET manage). No alcanza `display_mode=map`
 * (Alma Sierra nace en mapa sin georef staff).
 * @param {object|null|undefined} project
 * @returns {boolean}
 */
export function hasCadGeorefAnchor(project) {
  return Boolean(project?.ref_lot_id || project?.georef);
}

/**
 * UUID del lote madre (`lots.id`) para el form. El GET manage siempre
 * manda `ref_lot_id` (UUID o `null`). No usar `project.id` ni Feature.id.
 * @param {object|null|undefined} project
 * @returns {string}
 */
export function readCadRefLotIdForForm(project) {
  const id = project?.ref_lot_id;
  return isCadRefLotId(id) ? String(id).trim() : '';
}

/**
 * Tras Quitar, el GET trae `ref_lot_id: null`; esto pisa el ancla local.
 * @param {object|null|undefined} project
 * @returns {object|null|undefined}
 */
export function stripCadGeorefAnchor(project) {
  if (!project) return project;
  return { ...project, ref_lot_id: null, georef: null };
}

/**
 * Body para sacar el plano del mapa: borra lote, puntos y geom_4326.
 * Requiere `id` del proyecto (`?slug=` solo → 400).
 * @param {{ id?: string }} input
 * @returns {{ id?: string, ref_lot_id: null }}
 */
export function buildCadClearGeorefPatch({ id } = {}) {
  const body = { ref_lot_id: null };
  const trimmedId = String(id || '').trim();
  if (trimmedId) body.id = trimmedId;
  return body;
}

/**
 * Body parcial de `PATCH /updatecadproject` (georef).
 * Sticker → `georef_points` (3 pares). `src` = metros de
 * `georef_src_corners`; `lnglat` = mapa. Si hay lote madre a mano, también
 * `ref_lot_id` (`lots.id`). Sin ID, el API conserva o infiere.
 * Requiere `id` del proyecto.
 * @param {{ id?: string, refLotId?: string, points?: object[], sticker?: object|null }} input
 * @returns {{ id?: string, ref_lot_id?: string, georef_points?: object[] }}
 */
export function buildCadGeorefPatch({ id, refLotId, points, sticker } = {}) {
  const body = {};
  const trimmedId = String(id || '').trim();
  if (trimmedId) body.id = trimmedId;
  if (isCadRefLotId(refLotId)) {
    body.ref_lot_id = String(refLotId).trim();
  }
  const fromSticker = georefPointsFromSticker(sticker);
  const complete = completeCadGeorefPoints(fromSticker.length ? fromSticker : points);
  if (complete.length >= CAD_GEOREF_MIN_POINTS) {
    body.georef_points = complete;
  }
  return body;
}

/**
 * Qué tiene que viajar cuando el dueño aprieta Guardar en Referenciar.
 *
 * En el panel nada impacta hasta ese momento: elegir, quitar y sacar del mapa
 * son estado local. Acá se traduce ese borrador a llamadas, en orden (primero
 * la imagen, después el proyecto) y sin PATCHs redundantes.
 *
 * `displayMode` se manda explícito cuando el resultado **no** es el mapa: el
 * API deriva `canvas` / `image` por su cuenta y esa derivación es justo la que
 * movía la vista sin que el dueño confirmara.
 *
 * @param {{
 *   uploadedImage?: { s3Key?: string, s3Url?: string }|null,
 *   removed?: boolean,
 *   clearFromMap?: boolean,
 *   hasRemoteImage?: boolean,
 *   wasOnMap?: boolean,
 *   sticker?: object|null,
 * }} input
 * @returns {{
 *   saveImage: { s3Key: string, s3Url: string }|null,
 *   deleteImage: boolean,
 *   georef: 'set'|'clear'|null,
 *   displayMode: 'map'|'canvas'|'image'|null,
 *   hasImageAfter: boolean,
 *   onMapAfter: boolean,
 *   sendTransform: boolean,
 *   empty: boolean,
 * }}
 */
export function planCadGeorefCommit({
  uploadedImage = null,
  removed = false,
  clearFromMap = false,
  hasRemoteImage = false,
  wasOnMap = false,
  sticker = null,
} = {}) {
  const s3Key = String(uploadedImage?.s3Key || '').trim();
  const s3Url = String(uploadedImage?.s3Url || '').trim();
  const saveImage = s3Key || s3Url ? { s3Key, s3Url } : null;
  const deleteImage = Boolean(removed && hasRemoteImage && !saveImage);

  let hasImageAfter = hasRemoteImage;
  if (saveImage) hasImageAfter = true;
  else if (deleteImage) hasImageAfter = false;

  let georef = null;
  if (clearFromMap) georef = 'clear';
  else if (sticker) georef = 'set';

  const onMapAfter = clearFromMap ? false : Boolean(sticker || wasOnMap);
  const displayMode = onMapAfter ? null : (hasImageAfter ? 'image' : 'canvas');

  return {
    saveImage,
    deleteImage,
    georef,
    displayMode,
    hasImageAfter,
    onMapAfter,
    sendTransform: hasImageAfter,
    empty: !saveImage && !deleteImage && !georef && !displayMode,
  };
}

/**
 * Un solo body de `PATCH /updatecadproject` para todo lo que confirma Guardar:
 * georef (o su clear), vista final y encaje del plano base.
 *
 * `canvas_image_transform` (`docs/maps/cad-canvas-image-transform-backend-spec.md`):
 * `dx`, `dy`, `scale`, `scaleX`, `scaleY`, `rotationRad`. El GET los devuelve
 * y hornea `map_coordinates`; el overlay **no** pinta ese quad si puede
 * recalcular contain + transform + georef (igual que Referenciar).
 *
 * @param {{
 *   id?: string,
 *   plan?: object,
 *   refLotId?: string,
 *   sticker?: object|null,
 *   transform?: object|null,
 *   showPlanImage?: boolean,
 * }} input
 * @returns {object|null} body del PATCH, o `null` si no hay nada que mandar
 */
export function buildCadProjectCommitPatch({
  id,
  plan,
  refLotId,
  sticker,
  transform,
  showPlanImage,
} = {}) {
  if (!plan) return null;
  let body = null;
  if (plan.georef === 'clear') {
    body = buildCadClearGeorefPatch({ id });
  } else if (plan.georef === 'set') {
    body = buildCadGeorefPatch({ id, refLotId, sticker });
  } else if (plan.displayMode || plan.sendTransform) {
    const trimmedId = String(id || '').trim();
    body = trimmedId ? { id: trimmedId } : {};
  }
  if (plan.hasImageAfter && typeof showPlanImage === 'boolean') {
    if (!body) {
      const trimmedId = String(id || '').trim();
      body = trimmedId ? { id: trimmedId } : {};
    }
    body.show_plan_image = showPlanImage;
  }
  if (!body) return null;
  if (plan.displayMode) body.display_mode = plan.displayMode;
  if (plan.sendTransform) {
    body.canvas_image_transform = normalizeCadCanvasImageTransform(transform);
  }
  return body;
}
